import { Promotion } from '@/@type/promotion';
import { PromotionType, ResultingPromotion } from '@/__generated__/types';
import { useSnackbarHelper } from '@/components/useSnackbarHelper';
import { isValidIndex } from '@/utils/checkVariables';
import { Box, Button, Grid2, Paper } from '@mui/material';
import { FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { CommonInput } from '../formik/commonInput';
import { Label } from '../styles';
import { createPromoValidator, initLuckyDrawReward } from '../tools';
import { PromoSelector } from './PromoSelector';

interface ILuckyDrawRewardsProps {
    formik: FormikProps<Promotion>;
}

export const LuckyDrawRewards: React.FC<ILuckyDrawRewardsProps> = (props: ILuckyDrawRewardsProps) => {
    const { formik } = props;
    const { values } = formik;
    const { resultingPromotions = [] } = values;
    const { t } = useTranslation();
    const { showSnackbar } = useSnackbarHelper();

    const handleAddRewardOnClick = (): void => {
        const newReward = initLuckyDrawReward();
        const newRewards = [...(resultingPromotions || []), newReward];
        formik.setFieldValue('resultingPromotions', newRewards);
    };

    const getLuckyDrawRewardPath = (index: number, key: keyof ResultingPromotion): string => {
        if (!resultingPromotions) {
            console.error('resultingPromotions is undefined');
            showSnackbar('resultingPromotions is undefined', 'error');
            return '';
        }
        if (!isValidIndex(index, resultingPromotions)) {
            console.error(`Invalid index: ${index}`);
            showSnackbar(`Invalid index: ${index}`, 'error');
            return '';
        }
        return `resultingPromotions[${index}].${key}`;
    };
    const handleRemoveBtnOnClick = (index: number): void => {
        if (!resultingPromotions) {
            return;
        }
        if (resultingPromotions.length === 1) {
            formik.setFieldValue('resultingPromotions', []);
            return;
        }
        if (!isValidIndex(index, resultingPromotions)) {
            showSnackbar('Invalid index', 'error');
            return;
        }
        const newRewards = [...resultingPromotions];
        newRewards.splice(index, 1);
        formik.setFieldValue('resultingPromotions', newRewards);
    };

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'end',
                    alignItems: 'center',
                    padding: '16px'
                }}
            >
                <Button onClick={handleAddRewardOnClick} variant="contained">
                    Add
                </Button>
            </Box>
            {resultingPromotions.map((reward, index) => {
                return (
                    <Paper elevation={3} key={index} sx={{ padding: '16px' }}>
                        <Grid2 container spacing={1}>
                            <Grid2 size={{ xs: 12, sm: 9 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                    <Label>{t('Reward')}</Label>
                                    <PromoSelector
                                        formik={formik}
                                        formikPath={getLuckyDrawRewardPath(index, 'promotionId')}
                                        promoValidator={createPromoValidator([PromotionType.LuckyDraw])}
                                        multiple={false}
                                    />
                                </Box>
                            </Grid2>
                            <Grid2 size={{ xs: 6, sm: 2 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                    <CommonInput
                                        formik={formik}
                                        fieldPath={`resultingPromotions[${index}].winPercentage`}
                                        label="Winning Probability"
                                        type="number"
                                        placeholder="Winning Probability"
                                        endAdornment="%"
                                    />
                                </Box>
                            </Grid2>
                            <Grid2 size={{ xs: 6, sm: 1 }} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <Button onClick={() => handleRemoveBtnOnClick(index)} variant="contained" color="error">
                                    Remove
                                </Button>
                            </Grid2>
                        </Grid2>
                    </Paper>
                );
            })}
        </>
    );
};
